import React, { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getHomeBlurPlaceholder } from "@/utils/homeBlurPlaceholders";

const MomentsOfJoy = () => {
  const [selectedImages, setSelectedImages] = useState<string[]>([]);

  // Safe hook usage with fallback
  let t: (key: string) => string;
  try {
    const { t: useLanguageT } = useLanguage();
    t = useLanguageT;
  } catch (error) {
    // Fallback to English translations if context is not available
    t = (key: string) => {
      const fallbackTranslations: Record<string, string> = {
        "moments.title": "Moments of Joy at Beatenberg Resort & Spa",
        "moments.description":
          "Discover the beautiful moments and experiences that make every stay at our resort truly special",
        "moments.showNewMoments": "Show New Moments",
      };
      return fallbackTranslations[key] || key;
    };
  }

  // All available images from the Moments of joy folder
  const allImages = [
    "IMG-20250813-WA0258.jpg",
    "IMG-20250813-WA0288.jpg",
    "IMG-20250813-WA0164.jpg",
    "IMG-20250813-WA0145.jpg",
    "IMG-20250813-WA0060.jpg",
    "IMG-20250813-WA0061.jpg",
    "IMG-20250813-WA0133.jpg",
    "IMG-20250813-WA0042.jpg",
    "IMG-20250813-WA0041.jpg",
    "IMG-20250813-WA0036.jpg",
    "IMG-20250813-WA0028.jpg",
    "IMG-20250813-WA0015.jpg",
    "IMG-20250813-WA0016.jpg",
    "IMG-20250812-WA0037.jpg",
    "IMG-20250812-WA0024.jpg",
    "IMG-20250812-WA0063.jpg",
    "IMG-20250812-WA0028.jpg",
    "IMG-20250812-WA0020.jpg",
    "IMG-20250812-WA0066.jpg",
  ];

  // Function to randomly select 6 images
  const selectRandomImages = () => {
    const shuffled = [...allImages].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 6);
  };

  // Set random images on component mount
  useEffect(() => {
    setSelectedImages(selectRandomImages());
  }, []);

  return (
    <section className="py-12 bg-elegant-white" data-oid="k8uylhr">
      <div className="container mx-auto px-6" data-oid="67kj95-">
        {/* Section Header */}
        <div className="text-center mb-10" data-oid="i8ph5_p">
          <h2
            className="text-4xl md:text-5xl text-gray-900 font-playfair mb-6 uppercase"
            data-oid="ytuzrpk"
          >
            {t("moments.title")}
          </h2>
          <p
            className="text-lg text-gray-900 font-lato max-w-3xl mx-auto"
            data-oid="53wzlke"
          >
            {t("moments.description")}
          </p>
        </div>

        {/* Fixed Grid Layout */}
        <div
          className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto"
          data-oid="nt9_e9o"
        >
          {selectedImages.map((image, index) => (
            <div
              key={`${image}-${index}`}
              className="group cursor-pointer"
              data-oid="9gvo0.m"
            >
              <div
                className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 aspect-square"
                data-oid="t5d-qsh"
              >
                <OptimizedImage
                  src={`/images/Homepage Shortlisted media/Moments of joy/${image.replace(".jpg", ".optimized.webp")}`}
                  alt={`Moment of joy ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  priority={false}
                  fetchPriority="auto"
                  blurDataURL={getHomeBlurPlaceholder(`/images/Homepage Shortlisted media/Moments of joy/${image.replace(".jpg", ".optimized.webp")}`)}
                  data-oid="3-i.vvy"
                />

                {/* Overlay on hover */}
                <div
                  className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  data-oid="i428gi7"
                />

                {/* Subtle border */}
                <div
                  className="absolute inset-0 border-2 border-white/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  data-oid="hbqa2k3"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Refresh Button */}
        <div className="text-center mt-8" data-oid="ucdq._v">
          <button
            onClick={() => setSelectedImages(selectRandomImages())}
            className="inline-flex items-center px-8 py-3 bg-[#A38648] text-white font-semibold rounded-full hover:bg-[#8B6F3A] transition-colors duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            data-oid="4rb.8ki"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              data-oid="e.v1lsx"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                data-oid=".9izppt"
              />
            </svg>
            {t("moments.showNewMoments")}
          </button>
        </div>
      </div>
    </section>
  );
};

export default MomentsOfJoy;
